import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextAreaField, TextField } from '../../components/Field';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import {
  stateOf,
  useApiTokens,
  useCreateApiToken,
  useGrantablePermissions,
  useRevokeApiToken,
  type ApiTokenRead,
} from './queries';

/**
 * Die Zugangstoken des Mandanten -- F6-01.
 *
 * **Warum diese Seite ueberhaupt gebraucht wird.** Ein Token, der auf dem
 * Server von Hand angelegt wird, hat niemand im Blick: Er steht in keiner
 * Liste, sein Ablauf faellt niemandem auf, und beim Ausscheiden eines
 * Dienstleisters weiss keiner, welche Zugaenge zu widerrufen waeren. Genau
 * das ist der Befund, den eine Pruefung sucht -- und die Seite hier ist die
 * Antwort darauf.
 *
 * **Das Geheimnis steht einmal da.** Wie das Erstkennwort in ``UsersPage``:
 * Abgelegt ist nur, was der Token darf, nicht, wie er lautet. Wer ihn
 * wegklickt, bevor er ihn uebernommen hat, widerruft ihn und laesst einen
 * neuen ausstellen. Das ist unbequem und ausdruecklich so gewollt -- ein
 * nachschlagbares Geheimnis waere eines, das auch jemand nachschlaegt, der
 * es nicht bekommen sollte.
 *
 * **Widerrufene bleiben stehen.** Wer prueft, warum ein Zugriff nicht mehr
 * geht, sucht genau diese Zeile. Sie verschwinden zu lassen hiesse, die
 * Antwort auf die haeufigste Frage zu loeschen.
 */
export function ApiTokensPage() {
  const { t, i18n } = useTranslation();
  const tokens = useApiTokens();
  const grantable = useGrantablePermissions();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  // Neunzig Tage als Vorschlag und nicht als Vorgabe: Eine Frist, die im
  // Betrieb unangekuendigt ablaeuft, wird beim naechsten Mal durch einen
  // unbefristeten Token ersetzt -- und dann ist nichts gewonnen.
  const [days, setDays] = useState('90');

  const alle = tokens.data ?? [];
  const vergebbar = grantable.data?.granted ?? [];
  const gesperrt = grantable.data?.forbidden ?? [];

  // Steht bis zum naechsten Vorgang. Er wird nicht nachgeladen -- es gibt
  // nichts nachzuladen.
  const geheimnis = create.data?.token ?? null;
  const geheimnisFuer = create.data?.token_record.name;

  function toggle(permission: string) {
    setSelected((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    );
  }

  function ablaufAlsIso(): string | null {
    const zahl = Number.parseInt(days, 10);
    if (!Number.isFinite(zahl) || zahl <= 0) return null;
    const ablauf = new Date();
    ablauf.setDate(ablauf.getDate() + zahl);
    return ablauf.toISOString();
  }

  return (
    <>
      <PageHeader title={t('apiTokens.title')} subtitle={t('apiTokens.subtitle')} />

      {tokens.isPending ? <SkeletonTable columns={6} /> : null}
      {tokens.isError ? <ErrorNotice error={tokens.error} /> : null}
      {grantable.isError ? <ErrorNotice error={grantable.error} /> : null}
      {create.isError ? <ErrorNotice error={create.error} /> : null}
      {revoke.isError ? <ErrorNotice error={revoke.error} /> : null}

      {/* Das Geheimnis. Einmal, und danach nirgends mehr. */}
      {geheimnis ? (
        <section className="card notice--vip" role="status">
          <h2>{t('apiTokens.secret')}</h2>
          <p className="table__muted">{t('apiTokens.secretFor', { name: geheimnisFuer })}</p>
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              userSelect: 'all',
              wordBreak: 'break-all',
            }}
          >
            {geheimnis}
          </p>
          <p className="field__hint">{t('apiTokens.secretHint')}</p>
          <h3>{t('apiTokens.usage')}</h3>
          <pre style={{ overflowX: 'auto' }}>
            <code>
              {`curl -H "Authorization: Bearer ${geheimnis}" \\\n  ${window.location.origin}/api/v1/tickets`}
            </code>
          </pre>
        </section>
      ) : null}

      <section className="card">
        <h2>{t('apiTokens.new')}</h2>
        <p className="table__muted">{t('apiTokens.intro')}</p>

        <div className="row">
          <TextField
            label={t('apiTokens.name')}
            value={name}
            hint={t('apiTokens.nameHint')}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <TextField
            label={t('apiTokens.expires')}
            type="number"
            min={0}
            max={365}
            value={days}
            hint={t('apiTokens.expiresHint')}
            onChange={(event) => {
              setDays(event.target.value);
            }}
          />
        </div>

        <TextAreaField
          label={t('apiTokens.description')}
          value={description}
          hint={t('apiTokens.descriptionHint')}
          rows={2}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />

        <fieldset className="field">
          <legend className="field__label">{t('apiTokens.permissions')}</legend>
          <span className="field__hint">{t('apiTokens.permissionsHint')}</span>
          <div className="row">
            {vergebbar.map((permission) => (
              <label key={permission} className="field__inline">
                <input
                  type="checkbox"
                  checked={selected.includes(permission)}
                  onChange={() => {
                    toggle(permission);
                  }}
                />{' '}
                <code>{permission}</code>
              </label>
            ))}
          </div>

          {/* Die gesperrten stehen sichtbar daneben und werden nicht
              verschwiegen: Wer sie sucht und nicht findet, haelt es fuer
              einen Fehler. Wer liest, warum sie fehlen, hat verstanden,
              was ein Token ist. */}
          {gesperrt.length > 0 ? (
            <p className="field__hint">
              {t('apiTokens.forbiddenHint')}{' '}
              {gesperrt.map((permission) => (
                <code key={permission}>{permission} </code>
              ))}
            </p>
          ) : null}
        </fieldset>

        <Button
          variant="primary"
          disabled={!name.trim() || selected.length === 0}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              {
                name: name.trim(),
                description: description.trim(),
                permissions: selected,
                expires_at: ablaufAlsIso(),
              },
              {
                onSuccess: () => {
                  setName('');
                  setDescription('');
                  setSelected([]);
                },
              },
            );
          }}
        >
          {t('apiTokens.new')}
        </Button>
      </section>

      <section className="card card--flush">
        {alle.length === 0 ? (
          <EmptyState
            icon="apiTokens"
            title={t('apiTokens.empty')}
            hint={t('apiTokens.emptyHint')}
          />
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('apiTokens.name')}</th>
                  <th scope="col">{t('apiTokens.permissions')}</th>
                  <th scope="col">{t('apiTokens.status')}</th>
                  <th scope="col">{t('apiTokens.expires')}</th>
                  <th scope="col">{t('apiTokens.lastUsed')}</th>
                  <th scope="col">{t('tickets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {alle.map((token) => (
                  <TokenRow
                    key={token.id}
                    token={token}
                    language={i18n.language}
                    busy={revoke.isPending}
                    onRevoke={() => {
                      if (!window.confirm(t('apiTokens.revokeConfirm', { name: token.name })))
                        return;
                      revoke.mutate(token.id);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function TokenRow({
  token,
  language,
  busy,
  onRevoke,
}: {
  token: ApiTokenRead;
  language: string;
  busy: boolean;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  const zustand = stateOf(token);

  return (
    <tr>
      <td>
        {token.name}
        {token.description ? <div className="table__muted">{token.description}</div> : null}
      </td>
      <td className="table__muted">
        {token.permissions.map((permission) => (
          <code key={permission}>{permission} </code>
        ))}
      </td>
      <td>
        {zustand === 'active' ? (
          <Badge tone="success">{t('apiTokens.statusActive')}</Badge>
        ) : zustand === 'revoked' ? (
          <Badge tone="neutral">{t('apiTokens.statusRevoked')}</Badge>
        ) : (
          <Badge tone="warning">{t('apiTokens.statusExpired')}</Badge>
        )}
      </td>
      <td className="table__muted">
        {token.expires_at
          ? formatDateTime(token.expires_at, language)
          : t('apiTokens.expiresNever')}
      </td>
      <td className="table__muted">
        {token.last_used_at
          ? formatDateTime(token.last_used_at, language)
          : t('apiTokens.neverUsed')}
      </td>
      <td>
        {zustand === 'revoked' ? null : (
          <Button variant="ghost" busy={busy} onClick={onRevoke}>
            {t('apiTokens.revoke')}
          </Button>
        )}
      </td>
    </tr>
  );
}
